<template>
  <div class="fixed inset-0 flex items-center justify-center  bg-opacity-50 drop-shadow-2xl bg-black z-50">
    <div class="bg-white p-8 rounded shadow-md w-full max-w-2xl dark:bg-dark-modal">
      <h2 class="text-3xl font-bold mb-6 text-center dark:text-dark-text">Create new Room</h2>
      <p v-if="roomError" class="text-red-500 text-sm mt-1">{{ roomError }}</p>
      <div>
        <div class="mb-4">
          <label for="roomName" class="block text-gray-700 mb-2 dark:text-dark-text">Enter room name</label>
          <input
              type="text"
              id="roomName"
              placeholder="Room name"
              v-model="roomName"
              class="w-full p-2 border rounded dark:bg-transparent dark:border-dark-border dark:focus:border-gray-100 dark:text-dark-text"
              :class="{'border-red-500': roomError}"
              maxlength="100"
              required
          />
        </div>

        <div class="mb-4 border border-gray-200 rounded p-4 dark:border-dark-border">
          <label class="block text-gray-700 mb-2 dark:text-dark-text">Customize fibonacci values</label>
          <div class="flex flex-wrap gap-x-3 dark:text-dark-text">
            <div
                @click="selectCard(card)"
                v-for="(card, index) in cards" :key="index"
                class="mr-4 mb-2 py-1 px-2 flex gap-1 border rounded-lg border-gray-200 dark:border-gray-100/20 cursor-pointer">
              <label class="card-item">
                <input
                    type="checkbox"
                    :id="'card-' + index"
                    :checked="card.selected"
                    :value="card.value"
                    class="mr-2 w-5 h-5"
                />
                <span class="w-5 h-5"></span>
              </label>

              <span class="font-bold">{{ card.value }}</span>
            </div>
          </div>
        </div>

        <div @click="toggleAllCards"
             role="button"
             class="relative flex gap-1 items-center mb-4 cursor-pointer">
          <label class="card-item">
            <input type="checkbox" id="useAllCards" :checked="useAllCards">
            <span class="w-5 h-5"/>
          </label>
          <span class="text-gray-700 dark:text-dark-text">Use all cards</span>
        </div>

        <div
            role="button"
            @click="selectDefaultCards" class="flex gap-1 items-center mb-4 cursor-pointer">
          <label class="card-item">
            <input type="checkbox" id="useAllCards" :checked="useDefaultCards">
            <span class="w-5 h-5"/>
          </label>
          <span class="text-gray-700 dark:text-dark-text">Use default cards</span>
        </div>


        <template v-if="false">
          <div class="mb-4">
            <input type="checkbox" id="enterStories" v-model="enterStories">
            <label for="enterStories" class="text-gray-700 ml-2">Do you want to enter stories in this room?</label>
          </div>

          <div class="mb-4">
            <input type="checkbox" id="confirmationSkipStories" v-model="confirmationSkipStories">
            <label for="confirmationSkipStories" class="text-gray-700 ml-2">Request confirmation when skipping
              stories?</label>
          </div>

          <div class="mb-4">
            <input type="checkbox" id="realTimeVoting" v-model="realTimeVoting">
            <label for="realTimeVoting" class="text-gray-700 ml-2">Do you want observers to see other players voting in
              real time?</label>
          </div>

          <div class="mb-4">
            <input type="checkbox" id="revealVotes" v-model="revealVotes">
            <label for="revealVotes" class="text-gray-700 ml-2">Do you want to reveal individual votes when voting
              completed?
            </label>
          </div>

          <div class="mb-4">
            <input type="checkbox" id="allowVoteChange" v-model="allowVoteChange">
            <label for="allowVoteChange" class="text-gray-700 ml-2">Allow players to change vote after scores
              shown?
            </label>
          </div>

          <div class="mb-4">
            <input type="checkbox" id="useCountdownTimer" v-model="useCountdownTimer">
            <label for="useCountdownTimer" class="text-gray-700 ml-2">Do you want to use a countdown timer?</label>
          </div>
        </template>

        <div class="flex gap-2 w-full">
          <Button
              type="button"
              @click="handleCancel"
              variant="outline"
              title="Cancel"
          />
          <Button
              type="button"
              @click="handleSubmit"
              variant="primary"
              title="Create"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cards from "@/library/cards";
import FBApp from "@/library/firebase";
import axios from "@/library/request";
import {nextTick} from "vue";
import {toast} from 'vue3-toastify';
import Button from '@/components/Button';

const {logEvents} = FBApp;
export default {
  emits: ['success', 'close'],
  components: {Button},
  data() {
    return {
      roomName: '',
      roomError: '',
      cards,
      useAllCards: false,
      useDefaultCards: true,
      enterStories: true,
      confirmationSkipStories: false,
      realTimeVoting: true,
      revealVotes: true,
      allowVoteChange: false,
      useCountdownTimer: false,
    };
  },
  computed:{
    selectedCards(){
      return this.cards.filter(item=>item?.selected);
    }
  },
  methods: {
    selectCard(card) {
      card.selected = !card.selected;
    },
    handleCancel() {
      this.$emit('close')
    },
    handleSubmit() {
      if (!this.roomName) {
        this.roomError = 'Room name is required';
        return;
      }

      if (this.roomName.length > 100) {
        this.roomError = 'Room name cannot exceed 100 characters';
        return;
      }

      if (this.selectedCards.length <= 1) {
        this.roomError = 'Card values is required at least 2 cards selected';
        return;
      }


      const payload = {
        title: this.roomName,
        fibonacci: this.selectedCards.map(item=>item.id)
      }

      axios.post('/room/store', payload).then(({room}) => {
        toast.success('Saved Successfully!');
        this.$emit('success', room);
        logEvents(`Create Room ${ room.id }`);
        this.roomError = '';
      }).catch((error) => {
        toast.error(error.message);
      });
    },

    toggleAllCards() {
      this.useAllCards = !this.useAllCards
      nextTick(() => {
        if (this.useAllCards) {
          return this.selectAllCards();
        }

        this.selectAllCards(false)
      })
    },
    selectAllCards(value = true) {
      this.useDefaultCards = false
      this.useAllCards = value
      this.cards.forEach(card => {
        card.selected = value
      });
    },
    selectDefaultCards() {
      this.useAllCards = false
      this.useDefaultCards = true
      const defaultCards = [1, 2, 3, 5, 8, 13, '?', 'Coffee'];
      this.cards.forEach(card => {
        card.selected = !!defaultCards.includes(card.value)
      });
      console.log(this.selectedCards);
    },
  },
  beforeMount() {
    if (this.useDefaultCards) {
      this.selectDefaultCards()
    }
  },
};
</script>


<style scoped>
.card-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  z-index: 0;
  pointer-events: none;
}

.card-item input {
  display: none; /* hide default checkbox */
}

.card-item span {
  border: 2px solid #cbd5e0; /* gray-200 */
  border-radius: 4px;
  display: inline-block;
  margin-right: 8px;
  background-color: #e5e7eb; /* gray-200 */
  transition: background-color 0.2s, border-color 0.2s;
  position: relative;
}

.card-item input:checked + span {
  background-color: #7e49e9; /* blue for selected */
  border-color: #7e49e9;
}

.card-item input:checked + span::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 0px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
</style>
